// Daily / Routines — web port of the mobile RoutineScreen. Lets users create
// recurring routines with either fixed times or an interval+window, optionally
// flagged as critical (medication / allergy / health / other) with dose notes.
//
// Backed by routineService.js → the shared Django routine endpoints, so data is
// consistent with the mobile app. Built entirely on design tokens so it themes
// correctly in light + dark (unlike the legacy dashboard CSS).
//
// "Done today" is a client-only affordance (localStorage, resets at midnight),
// matching the mobile app which also doesn't persist daily ticks to the server.
// Interval reminders are scheduled by the mobile app via Expo; on web we don't
// replicate push scheduling — routines are managed/visualized here.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, MoreVertical, Check, Bell, Calendar, Pencil, Trash2,
  Power, CalendarPlus, X, Clock,
} from 'lucide-react';
import {
  fetchRoutines, createRoutine, updateRoutine, deleteRoutine, instantiateRoutine,
} from '../services/routineService';
import FloatingComposer from './FloatingComposer';
import './RoutinesScreen.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_LETTER = { Mon: 'M', Tue: 'T', Wed: 'W', Thu: 'T', Fri: 'F', Sat: 'S', Sun: 'S' };
const INTERVAL_PRESETS = [
  { min: 30, label: '30 min' },
  { min: 60, label: '1 hr' },
  { min: 120, label: '2 hrs' },
  { min: 180, label: '3 hrs' },
  { min: 240, label: '4 hrs' },
  { min: 360, label: '6 hrs' },
];
const CRITICAL_TYPES = [
  { key: 'medication', emoji: '💊', label: 'Medication' },
  { key: 'allergy', emoji: '⚠️', label: 'Allergy' },
  { key: 'health', emoji: '🩺', label: 'Health' },
  { key: 'other', emoji: '📌', label: 'Other' },
];
const DOSE_PLACEHOLDER = {
  medication: 'e.g. 1 tablet, 500mg, 2 capsules',
  allergy: 'e.g. peanuts — call 911 if exposed',
  health: 'e.g. measure BP, log fasting glucose',
  other: 'e.g. additional details',
};

// ── time helpers ──────────────────────────────────────────────────────────
// Convert a 24h "HH:MM" (from <input type="time">) to mobile's "hh:mm AM/PM".
function to12h(hhmm) {
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const m = (mStr || '00').padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
}
// Inverse: "hh:mm AM/PM" → 24h "HH:MM" for the input value.
function to24h(label) {
  if (!label) return '';
  const m = label.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = (m[3] || '').toUpperCase();
  if (ampm === 'PM' && h < 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}`;
}
function windowStr(hour, ampm) {
  return `${parseInt(hour, 10)}:00 ${ampm}`;
}
function parseWindow(str, fallback) {
  if (!str) return fallback;
  const m = str.trim().match(/(\d{1,2}):?\d{0,2}\s*(AM|PM)?/i);
  if (!m) return fallback;
  return { hour: parseInt(m[1], 10) || fallback.hour, ampm: (m[2] || fallback.ampm).toUpperCase() };
}
function compactTime(s) {
  return (s || '').replace(':00', '').replace(/\s/g, '').toUpperCase();
}
function intervalLabel(min) {
  if (!min) return '';
  return min < 60 ? `${min}m` : `${Number((min / 60).toFixed(1)).toString().replace('.0', '')}h`;
}
function parseDays(csv) {
  if (!csv || csv === '[]') return [...DAYS];
  return csv.split(',').map((d) => d.trim()).filter((d) => DAYS.includes(d));
}
function scheduleSummary(r) {
  const days = parseDays(r.days_of_week);
  const daysLabel = days.length === 7 ? 'Every day'
    : days.length === 0 ? 'No days'
      : days.join(' · ');
  let timeLabel;
  if (r.interval_minutes) {
    const win = r.window_start && r.window_end
      ? ` · ${compactTime(r.window_start)}→${compactTime(r.window_end)}` : '';
    timeLabel = `Every ${intervalLabel(r.interval_minutes)}${win}`;
  } else {
    timeLabel = (r.time || '')
      .split(',').map((t) => t.trim()).filter(Boolean).join(' · ');
  }
  return { daysLabel, timeLabel };
}

// today's date params for instantiate + done-today key
function todayParams() {
  const d = new Date();
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    week: Math.ceil(d.getDate() / 7),
    day: d.toLocaleDateString('en-US', { weekday: 'long' }),
    iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
  };
}

const emptyForm = () => ({
  name: '',
  days: [...DAYS],
  timeMode: 'fixed',
  times: ['09:00'],
  intervalMin: 120,
  windowStart: { hour: 8, ampm: 'AM' },
  windowEnd: { hour: 10, ampm: 'PM' },
  isCritical: false,
  criticalType: '',
  dose: '',
});

function routineToForm(r) {
  const interval = !!r.interval_minutes;
  return {
    name: r.name || '',
    days: parseDays(r.days_of_week),
    timeMode: interval ? 'interval' : 'fixed',
    times: interval ? ['09:00']
      : ((r.time || '09:00 AM').split(',').map((t) => to24h(t)).filter(Boolean) || ['09:00']),
    intervalMin: r.interval_minutes || 120,
    windowStart: parseWindow(r.window_start, { hour: 8, ampm: 'AM' }),
    windowEnd: parseWindow(r.window_end, { hour: 10, ampm: 'PM' }),
    isCritical: !!r.is_critical,
    criticalType: r.critical_type || '',
    dose: r.medication_dose || '',
  };
}

function formToPayload(form, existing) {
  const interval = form.timeMode === 'interval';
  const isMedication = form.isCritical && form.criticalType === 'medication';
  const payload = {
    name: form.name.trim(),
    days_of_week: form.days.length === 7 ? '' : form.days.join(','),
    frequency: 'daily',
    is_critical: form.isCritical,
    critical_type: form.isCritical ? form.criticalType : '',
    is_medication: isMedication,
    medication_dose: form.isCritical && form.criticalType ? form.dose.trim() : '',
    category: isMedication ? 'Medication' : form.isCritical ? 'Critical' : 'General',
    interval_minutes: interval ? form.intervalMin : null,
    window_start: interval ? windowStr(form.windowStart.hour, form.windowStart.ampm) : '',
    window_end: interval ? windowStr(form.windowEnd.hour, form.windowEnd.ampm) : '',
  };
  payload.time = interval
    ? payload.window_start
    : (form.times.filter(Boolean).map(to12h).join(',') || '09:00 AM');
  // Preserve the active flag on edit (omitting it leaves it unchanged anyway).
  if (existing) payload.is_active = existing.is_active;
  return payload;
}

function RoutinesScreen() {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [doneIds, setDoneIds] = useState([]);
  // Quick-add schedule options (the composer "+" panel): which days + what time.
  const [quickDays, setQuickDays] = useState([...DAYS]);
  const [quickTime, setQuickTime] = useState('09:00');

  const today = useMemo(() => todayParams(), []);
  const doneKey = `@tudu_routines_done_${today.iso}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRoutines(await fetchRoutines());
    } catch (_) {
      /* handled in service */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(doneKey);
      setDoneIds(raw ? JSON.parse(raw) : []);
    } catch (_) { setDoneIds([]); }
  }, [doneKey]);

  const persistDone = (ids) => {
    setDoneIds(ids);
    try { localStorage.setItem(doneKey, JSON.stringify(ids)); } catch (_) { /* ignore */ }
  };

  const ordered = useMemo(() => {
    // Critical first, then medication, then by name — matches mobile intent.
    return [...routines].sort((a, b) => {
      const score = (r) => (r.is_critical ? 0 : r.is_medication ? 1 : 2);
      return score(a) - score(b) || (a.name || '').localeCompare(b.name || '');
    });
  }, [routines]);

  const doneCount = ordered.filter((r) => doneIds.includes(r.id) && r.is_active).length;
  const activeCount = ordered.filter((r) => r.is_active).length;

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setModalOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm(routineToForm(r)); setMenuFor(null); setModalOpen(true); };

  const toggleDay = (d) => setForm((f) => ({
    ...f,
    days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d],
  }));
  const setTime = (i, v) => setForm((f) => ({ ...f, times: f.times.map((t, idx) => (idx === i ? v : t)) }));
  const addTime = () => setForm((f) => ({ ...f, times: [...f.times, '18:00'] }));
  const removeTime = (i) => setForm((f) => ({ ...f, times: f.times.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = formToPayload(form, editing);
      if (editing) await updateRoutine(editing.id, payload);
      else await createRoutine(payload);
      setModalOpen(false);
      await load();
    } catch (_) {
      /* handled in service */
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = (r) => {
    persistDone(doneIds.includes(r.id) ? doneIds.filter((id) => id !== r.id) : [...doneIds, r.id]);
  };
  const toggleActive = async (r) => {
    setMenuFor(null);
    await updateRoutine(r.id, { is_active: !r.is_active });
    await load();
  };
  const remove = async (r) => {
    setMenuFor(null);
    if (!window.confirm(`Delete routine "${r.name}"?`)) return;
    await deleteRoutine(r.id);
    await load();
  };
  const addToToday = async (r) => {
    setMenuFor(null);
    await instantiateRoutine(r.id, today);
    window.alert(`Added "${r.name}" to today's tasks.`);
  };

  const toggleQuickDay = (d) => setQuickDays((days) => (
    days.includes(d) ? days.filter((x) => x !== d) : [...days, d]
  ));

  // Quick-add from the bottom composer, using the "+" panel's day/time options
  // (defaults: every day at 9 AM). The "New routine" button still opens the full
  // editor for intervals / medication.
  const quickAddRoutine = async (name) => {
    const days = quickDays.length === 0 ? [...DAYS] : quickDays;
    await createRoutine({
      name,
      days_of_week: days.length === 7 ? '' : days.join(','),
      frequency: 'daily',
      time: to12h(quickTime) || '09:00 AM',
      is_critical: false,
      critical_type: '',
      is_medication: false,
      medication_dose: '',
      interval_minutes: null,
      window_start: '',
      window_end: '',
      category: 'General',
    });
    await load();
    // reset the quick options back to the defaults for the next add
    setQuickDays([...DAYS]);
    setQuickTime('09:00');
  };

  // Schedule options shown in the composer's "+" panel (days + time).
  const routineOptions = (
    <>
      <span className="fc-opt-label">Repeat on</span>
      <div className="fc-opt-row">
        {DAYS.map((d) => (
          <button
            key={d}
            type="button"
            className={`fc-chip fc-day${quickDays.includes(d) ? ' active' : ''}`}
            onClick={() => toggleQuickDay(d)}
          >
            {DAY_LETTER[d]}
          </button>
        ))}
      </div>
      <span className="fc-opt-label">Time</span>
      <div className="fc-opt-row">
        <input
          type="time"
          className="fc-time-input"
          value={quickTime}
          onChange={(e) => setQuickTime(e.target.value)}
        />
        <span className="routines-sub" style={{ margin: 0 }}>
          {quickDays.length === 7 ? 'Every day'
            : quickDays.length === 0 ? 'Every day'
              : quickDays.join(' · ')}
        </span>
      </div>
    </>
  );

  return (
    <div className="routines-screen" onClick={() => menuFor && setMenuFor(null)}>
      <div className="routines-inner">
        <header className="routines-head">
          <div>
            <p className="routines-sub">
              {activeCount > 0
                ? `${doneCount} of ${activeCount} done today`
                : 'Build habits that repeat on your schedule'}
            </p>
          </div>
          <button className="routines-new-btn" onClick={openCreate}>
            <Plus size={18} strokeWidth={2.5} /> New routine
          </button>
        </header>

        {loading ? (
          <div className="routines-empty">Loading routines…</div>
        ) : ordered.length === 0 ? (
          <div className="routines-empty">
            <Clock size={28} />
            <p>No routines yet. Create your first daily habit.</p>
          </div>
        ) : (
          <ul className="routines-list">
            {ordered.map((r) => {
              const { daysLabel, timeLabel } = scheduleSummary(r);
              const done = doneIds.includes(r.id);
              const accent = r.is_critical ? 'var(--tudu-warning)'
                : r.is_medication ? 'var(--cat-routine)' : 'var(--tudu-brand)';
              return (
                <li key={r.id} className={`routine-card${r.is_active ? '' : ' inactive'}`}>
                  <button
                    className={`routine-check${done ? ' done' : ''}`}
                    style={{ borderColor: accent, color: done ? '#fff' : accent }}
                    onClick={() => toggleDone(r)}
                    aria-label={done ? 'Mark not done' : 'Mark done'}
                  >
                    {done && <Check size={16} strokeWidth={3} />}
                  </button>

                  <div className="routine-info">
                    <div className="routine-name-row">
                      <span className="routine-name">{r.name}</span>
                      {r.is_critical && !r.is_medication && (
                        <span className="routine-badge critical"><Bell size={11} /> Critical</span>
                      )}
                      {r.is_medication && <span className="routine-badge med">MED</span>}
                      {!r.is_active && <span className="routine-badge off">Paused</span>}
                    </div>
                    <div className="routine-meta">
                      <span>{timeLabel}</span>
                      <span className="routine-dot">·</span>
                      <span>{daysLabel}</span>
                    </div>
                  </div>

                  <div className="routine-menu-wrap" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="routine-menu-btn"
                      onClick={() => setMenuFor(menuFor === r.id ? null : r.id)}
                      aria-label="Routine options"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {menuFor === r.id && (
                      <div className="routine-menu">
                        <div className="routine-menu-schedule">
                          <Calendar size={13} /> {daysLabel}
                        </div>
                        <button onClick={() => addToToday(r)}><CalendarPlus size={15} /> Add to today</button>
                        <button onClick={() => openEdit(r)}><Pencil size={15} /> Edit</button>
                        <button onClick={() => toggleActive(r)}>
                          <Power size={15} /> {r.is_active ? 'Pause' : 'Activate'}
                        </button>
                        <button className="danger" onClick={() => remove(r)}><Trash2 size={15} /> Delete</button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {modalOpen && (
        <RoutineModal
          form={form}
          setForm={setForm}
          editing={editing}
          saving={saving}
          onClose={() => setModalOpen(false)}
          onSave={save}
          toggleDay={toggleDay}
          setTime={setTime}
          addTime={addTime}
          removeTime={removeTime}
        />
      )}

      <FloatingComposer
        placeholder="Add a routine…"
        onSubmit={quickAddRoutine}
        optionsPanel={routineOptions}
      />
    </div>
  );
}

function RoutineModal({
  form, setForm, editing, saving, onClose, onSave, toggleDay, setTime, addTime, removeTime,
}) {
  const interval = form.timeMode === 'interval';
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setWindow = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  return (
    <div className="routine-modal-overlay" onClick={onClose}>
      <div className="routine-modal" onClick={(e) => e.stopPropagation()}>
        <div className="routine-modal-head">
          <h2>{editing ? 'Edit routine' : 'New routine'}</h2>
          <button className="routine-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="routine-modal-body">
          {/* Name */}
          <label className="routine-field">
            <span className="routine-field-label">Name</span>
            <input
              className="routine-input"
              value={form.name}
              autoFocus
              placeholder="e.g. Take vitamins"
              onChange={(e) => set({ name: e.target.value })}
            />
          </label>

          {/* Days */}
          <div className="routine-field">
            <span className="routine-field-label">Repeat on</span>
            <div className="routine-days">
              {DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`routine-day${form.days.includes(d) ? ' active' : ''}`}
                  onClick={() => toggleDay(d)}
                >
                  {DAY_LETTER[d]}
                </button>
              ))}
            </div>
            <span className="routine-hint">
              {form.days.length === 7 ? 'Every day'
                : form.days.length === 0 ? "No days selected — won't run"
                  : form.days.join(' · ')}
            </span>
          </div>

          {/* Time mode */}
          <div className="routine-field">
            <span className="routine-field-label">Reminder</span>
            <div className="routine-segment">
              <button
                type="button"
                className={!interval ? 'active' : ''}
                onClick={() => set({ timeMode: 'fixed' })}
              >At a time</button>
              <button
                type="button"
                className={interval ? 'active' : ''}
                onClick={() => set({ timeMode: 'interval' })}
              >Every few hours</button>
            </div>
          </div>

          {/* Fixed times */}
          {!interval && (
            <div className="routine-field">
              {form.times.map((t, i) => (
                <div className="routine-time-row" key={i}>
                  <input
                    type="time"
                    className="routine-input routine-time"
                    value={t}
                    onChange={(e) => setTime(i, e.target.value)}
                  />
                  {i > 0 && (
                    <button type="button" className="routine-time-remove" onClick={() => removeTime(i)}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              {form.times.length < 3 && (
                <button type="button" className="routine-add-time" onClick={addTime}>
                  <Plus size={14} /> Add another time
                </button>
              )}
            </div>
          )}

          {/* Interval */}
          {interval && (
            <>
              <div className="routine-field">
                <span className="routine-field-label">Every</span>
                <div className="routine-chips">
                  {INTERVAL_PRESETS.map((p) => (
                    <button
                      key={p.min}
                      type="button"
                      className={`routine-chip${form.intervalMin === p.min ? ' active' : ''}`}
                      onClick={() => set({ intervalMin: p.min })}
                    >{p.label}</button>
                  ))}
                </div>
              </div>
              <div className="routine-window">
                <WindowPicker label="Start" value={form.windowStart} onChange={(p) => setWindow('windowStart', p)} />
                <span className="routine-window-arrow">→</span>
                <WindowPicker label="End" value={form.windowEnd} onChange={(p) => setWindow('windowEnd', p)} />
              </div>
            </>
          )}

          {/* Critical */}
          <div className="routine-field">
            <label className="routine-toggle-row">
              <span>
                <span className="routine-field-label">Critical reminder</span>
                <span className="routine-hint">Highlight don't-miss routines like meds.</span>
              </span>
              <span className={`routine-switch${form.isCritical ? ' on' : ''}`} onClick={() => set({ isCritical: !form.isCritical })}>
                <span className="routine-switch-knob" />
              </span>
            </label>
          </div>

          {form.isCritical && (
            <div className="routine-field">
              <div className="routine-chips">
                {CRITICAL_TYPES.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`routine-chip${form.criticalType === c.key ? ' active' : ''}`}
                    onClick={() => set({ criticalType: form.criticalType === c.key ? '' : c.key })}
                  >{c.emoji} {c.label}</button>
                ))}
              </div>
              {form.criticalType && (
                <textarea
                  className="routine-input routine-textarea"
                  rows={2}
                  value={form.dose}
                  placeholder={DOSE_PLACEHOLDER[form.criticalType]}
                  onChange={(e) => set({ dose: e.target.value })}
                />
              )}
            </div>
          )}
        </div>

        <div className="routine-modal-foot">
          <button className="routine-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="routine-btn-primary"
            disabled={saving || !form.name.trim() || form.days.length === 0}
            onClick={onSave}
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create routine'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WindowPicker({ label, value, onChange }) {
  return (
    <div className="routine-window-col">
      <span className="routine-window-label">{label}</span>
      <div className="routine-window-controls">
        <select value={value.hour} onChange={(e) => onChange({ hour: parseInt(e.target.value, 10) })}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <select value={value.ampm} onChange={(e) => onChange({ ampm: e.target.value })}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}

export default RoutinesScreen;
